/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  type UseInteractionsReturn,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { tokens } from '@neo4j-ndl/base';
import { useMemo, useState } from 'react';

export interface SpotlightOptions {
  isInitialOpen?: boolean;
  placement?: Placement;
  isOpen?: boolean;
  onOpenChange?: (open: boolean, event?: Event) => void;
  anchorElement?: Element | null;
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  anchorElementAsPortalAnchor?: boolean;
  initialFocus?: number | React.MutableRefObject<HTMLElement | null>;
  // TODO v5: fix boolean naming convention
  // eslint-disable-next-line @typescript-eslint/naming-convention
  closeOnClickOutside?: boolean;
  strategy?: 'fixed' | 'absolute';
}

export interface UseSpotlightReturn
  extends UseInteractionsReturn, ReturnType<typeof useFloating> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  labelId: string | undefined;
  transitionStyles: React.CSSProperties;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  anchorElementAsPortalAnchor: boolean | undefined;
  initialFocus: number | React.MutableRefObject<HTMLElement | null> | undefined;
}

export function useSpotlight({
  isInitialOpen = false,
  placement = 'bottom',
  isOpen: controlledOpen,
  onOpenChange: setControlledOpen,
  anchorElement,
  anchorElementAsPortalAnchor,
  initialFocus,
  closeOnClickOutside,
  strategy = 'absolute',
}: SpotlightOptions = {}): UseSpotlightReturn {
  const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(isInitialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();
  const isOpen = controlledOpen ?? isOpenUncontrolled;
  const setIsOpen = setControlledOpen ?? setIsOpenUncontrolled;
  const data = useFloating({
    elements: {
      reference: anchorElement,
    },
    middleware: [
      offset(10),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
    onOpenChange: (open, event) => {
      setIsOpen(open);
      setControlledOpen?.(open, event);
    },
    open: isOpen,
    placement: placement,
    strategy,
    whileElementsMounted: autoUpdate,
  });
  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen === undefined,
  });

  const dismiss = useDismiss(context, {
    outsidePress: closeOnClickOutside,
  });
  const role = useRole(context, {
    role: 'dialog',
  });

  const interactions = useInteractions([click, dismiss, role]);

  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: Number.parseInt(tokens.motion.duration.quick) ?? 0,
  });

  return useMemo(
    () => ({
      isOpen,
      setIsOpen,
      ...interactions,
      ...data,
      anchorElementAsPortalAnchor,
      descriptionId,
      initialFocus,
      labelId,
      setDescriptionId,
      setLabelId,
      transitionStyles,
    }),
    [
      isOpen,
      setIsOpen,
      interactions,
      data,
      transitionStyles,
      labelId,
      anchorElementAsPortalAnchor,
      descriptionId,
      initialFocus,
    ],
  );
}
